// Партнёрская ссылка витрины: https://…/?promo=CODE (26.09.2026).
//
// Код запоминается на устройстве в момент перехода — покупатель редко
// регистрируется и покупает в тот же визит. Дальше он используется трижды:
//   1) переход засчитывается в воронку кода (`/promos/visit`);
//   2) при входе/регистрации по телефону код уходит в `/buyer/auth/verify` —
//      шлюз засчитывает покупателя партнёру, если он новый и ещё ничей;
//   3) в оформлении заказа код подставляется в поле «Промокод», если в
//      корзине ещё нет другого.
// Срок — 30 дней, как у источника заказа (lib/discovery-source.tsx).

import type { ReferralVisitMeta } from "@/api/types";
import { referralMetaOf } from "./referral-meta";

const STORAGE_KEY = "osmix-referral-promo";
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

// meta - канал (ссылка / QR) и метки перехода (27.10.2026).
type Stored = {
	code: string;
	at: number;
	visited?: boolean;
	appliedFor?: string;
	attemptedFor?: string;
	meta?: ReferralVisitMeta;
};

const read = (): Stored | null => {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const value = JSON.parse(raw) as Stored;
		if (!value?.code || Date.now() - value.at > TTL_MS) {
			window.localStorage.removeItem(STORAGE_KEY);
			return null;
		}
		return value;
	} catch {
		return null;
	}
};

const write = (value: Stored): void => {
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
	} catch {
		// Хранилище закрыто — ссылка просто не будет засчитана. Витрину не роняем.
	}
};

const normalize = (code: string): string => code.trim().toUpperCase().replace(/\s/g, "").slice(0, 64);

/** Запомнить код из адреса посадочной страницы. Новый код заменяет старый. */
export const captureReferralPromo = (search: string): string | null => {
	if (typeof window === "undefined") return null;
	const code = normalize(new URLSearchParams(search).get("promo") ?? "");
	if (!code) return null;
	const current = read();
	if (current?.code !== code) write({ code, at: Date.now(), meta: referralMetaOf(search) });
	return code;
};

export const readReferralPromo = (): string | undefined => read()?.code;

/** Канал и метки перехода по партнёрской ссылке. */
export const readReferralMeta = (): ReferralVisitMeta | undefined => read()?.meta;

/** Переход засчитан — второй раз с этого устройства не шлём (шлюз и так схлопнет за день). */
export const markReferralVisited = (): void => {
	const current = read();
	if (current) write({ ...current, visited: true });
};

export const referralVisitPending = (): boolean => {
	const current = read();
	return Boolean(current && !current.visited);
};

/**
 * Подставить код в оформлении можно один раз на корзину: покупатель мог
 * осознанно снять его, и возвращать код обратно на каждом открытии нельзя.
 */
export const referralApplyPendingFor = (cartId: string | null | undefined): string | undefined => {
	const current = read();
	if (!current || !cartId || current.appliedFor === cartId) return undefined;
	return current.code;
};

export const markReferralApplied = (cartId: string): void => {
	const current = read();
	if (current) write({ ...current, appliedFor: cartId });
};

/** Атрибуцию для этого аккаунта уже запрашивали — повторять на каждом открытии незачем. */
export const referralAttributePendingFor = (userKey: string): string | undefined => {
	const current = read();
	if (!current || current.attemptedFor === userKey) return undefined;
	return current.code;
};

export const markReferralAttempted = (userKey: string): void => {
	const current = read();
	if (current) write({ ...current, attemptedFor: userKey });
};
